/**
 * App 配置与 Protocol Scheme 映射
 * 用于支持多 App（CodeFlicker、Duet）的登录回调
 *
 * 使用 referrer 参数来标识登录来源和目标 App：
 * - referrer=plugin  → VSCode 插件发起，不显示"打开应用"按钮
 * - referrer=ide     → CodeFlicker IDE 发起，回调到 codeflicker://
 * - referrer=duet    → Duet App 发起，回调到 duet://
 * - referrer=空/其他  → 默认回调到 codeflicker://
 */

export type AppReferrer = 'ide' | 'duet' | 'plugin';

/**
 * 允许的 referrer 白名单（安全限制）
 */
export const ALLOWED_REFERRERS: readonly AppReferrer[] = ['ide', 'duet', 'plugin'] as const;

/**
 * Referrer 到 Protocol 的映射表（不带 ://）
 * plugin 不映射，因为插件场景不需要唤起 App
 */
export const REFERRER_PROTOCOL_MAP: Record<string, string> = {
  ide: 'codeflicker',
  duet: 'duet',
};

/**
 * 默认 Protocol（当 referrer 为空或不在映射表中时使用）
 */
export const DEFAULT_PROTOCOL = 'codeflicker';

/**
 * 验证 referrer 是否合法
 * @param referrer - 输入的 referrer 值
 * @returns 是否为合法的 referrer
 */
export function isValidReferrer(referrer?: string | null): boolean {
  if (!referrer) {
    return false;
  }
  return ALLOWED_REFERRERS.includes(referrer.toLowerCase() as AppReferrer);
}

/**
 * 根据 referrer 获取对应的 Protocol Scheme 基础名（不带 ://）
 * @param referrer - 来源标识 (ide | duet | plugin)
 * @returns Protocol scheme 基础名
 */
export function getAppProtocolBase(referrer?: string | null): string {
  if (!referrer) {
    return DEFAULT_PROTOCOL;
  }
  const normalized = referrer.toLowerCase();
  // 安全检查：只返回白名单内的 protocol
  if (normalized in REFERRER_PROTOCOL_MAP) {
    return REFERRER_PROTOCOL_MAP[normalized];
  }
  // 非法或未映射的 referrer，返回默认值
  return DEFAULT_PROTOCOL;
}

/**
 * 获取完整的 Protocol Scheme（带 ://）
 * @param referrer - 来源标识 (ide | duet)
 * @returns 完整的 Protocol scheme，如 "codeflicker://"
 */
export function getAppProtocol(referrer?: string | null): string {
  return `${getAppProtocolBase(referrer)}://`;
}

/**
 * 构建 App 唤起 URL
 * @param referrer - 来源标识 (ide | duet)，决定使用哪个 App 的 protocol
 * @param path - URL 路径 (如 login, open)
 * @param params - 查询参数
 * @returns 完整的 App URL，如 "codeflicker://login?token=xxx&source=web"
 */
export function buildAppUrl(
  referrer: string | null | undefined,
  path: string,
  params?: Record<string, string>,
): string {
  const protocol = getAppProtocol(referrer);
  const queryString = params ? `?${new URLSearchParams(params).toString()}` : '';
  return `${protocol}${path}${queryString}`;
}

/**
 * 判断是否应该显示"打开应用"按钮
 * @param referrer - 来源标识
 * @returns 如果是 plugin 来源则返回 false，否则返回 true
 */
export function shouldShowOpenAppButton(referrer?: string | null): boolean {
  return referrer?.toLowerCase() !== 'plugin';
}

/**
 * Referrer 到产品名称的映射表
 */
export const REFERRER_PRODUCT_NAME_MAP: Record<string, string> = {
  ide: 'CodeFlicker',
  duet: 'Duet',
};

/**
 * 默认产品名称
 */
export const DEFAULT_PRODUCT_NAME = 'CodeFlicker';

/**
 * 根据 referrer 获取产品名称
 * @param referrer - 来源标识 (ide | duet)
 * @returns 产品名称
 */
export function getProductName(referrer?: string | null): string {
  if (!referrer) {
    return DEFAULT_PRODUCT_NAME;
  }
  return REFERRER_PRODUCT_NAME_MAP[referrer.toLowerCase()] || DEFAULT_PRODUCT_NAME;
}
